// src/components/StudyNoteDetail.js
import React from 'react';
import { Container, Row, Col, Card, Badge, Button } from 'react-bootstrap';
import { useParams, Link } from 'react-router-dom';
import { studyNotes } from '../data/studyNotes';

function StudyNoteDetail() {
  const { id } = useParams();
  const note = studyNotes.find(note => note.id === parseInt(id));

  if (!note) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <h2>Study Note Not Found</h2>
          <p>The study note you're looking for doesn't exist.</p>
          <Button as={Link} to="/study-notes" variant="primary">
            Back to Study Notes
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      {/* Breadcrumb Navigation */}
      <nav aria-label="breadcrumb" className="mb-4">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/">Home</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to="/study-notes">Study Notes</Link>
          </li>
          <li className="breadcrumb-item active">{note.title}</li>
        </ol>
      </nav>

      <Row>
        <Col lg={8}>
          <article>
            <header className="mb-4">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <Badge bg="primary">{note.category}</Badge>
                <small className="text-muted">Published on {new Date(note.date).toLocaleDateString()}</small>
              </div>
              
              <h1 className="display-6 mb-3">{note.title}</h1>
              
              <div className="mb-3">
                {note.tags.map(tag => (
                  <Badge key={tag} bg="light" text="dark" className="me-1">
                    #{tag}
                  </Badge>
                ))}
              </div>
            </header>

            {/* YouTube Video */}
            {note.youtubeUrl && (
              <div className="mb-4">
                <h5>📹 Video Explanation</h5>
                <div className="ratio ratio-16x9 mb-3">
                  <iframe
                    src={note.youtubeUrl}
                    title={`YouTube video for ${note.title}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{border: 'none', borderRadius: '8px'}}
                  ></iframe>
                </div>
              </div>
            )}

            {/* Blog Content */}
            <div className="mb-4">
              <h5>📝 Written Notes</h5>
              <div className="content" style={{ lineHeight: '1.6' }}>
                {note.content.split('\n').map((paragraph, index) => {
                  const trimmed = paragraph.trim();
                  
                  // Handle headers
                  if (trimmed.startsWith('## ')) {
                    return <h4 key={index} className="mt-4 mb-3" style={{color: '#2c3e50'}}>{trimmed.replace('## ', '')}</h4>;
                  }
                  
                  // Handle bold text
                  if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
                    return <strong key={index} className="d-block my-2" style={{fontSize: '1.1em'}}>{trimmed.replace(/\*\*/g, '')}</strong>;
                  }
                  
                  // Handle list items
                  if (trimmed.startsWith('* ') || trimmed.startsWith('- ')) {
                    return (
                      <div key={index} className="d-flex align-items-start mb-1">
                        <span className="me-2">•</span>
                        <span>{trimmed.substring(2)}</span>
                      </div>
                    );
                  }
                  
                  // Handle numbered lists
                  if (/^\d+\.\s/.test(trimmed)) {
                    return (
                      <div key={index} className="d-flex align-items-start mb-1">
                        <span className="me-2" style={{minWidth: '25px'}}>{trimmed.split('.')[0]}.</span>
                        <span>{trimmed.substring(trimmed.indexOf('.') + 2)}</span>
                      </div>
                    );
                  }
                  
                  // Handle empty lines
                  if (trimmed === '') {
                    return <br key={index} />;
                  }
                  
                  // Handle regular paragraphs
                  return <p key={index} className="mb-3">{paragraph}</p>;
                })}
              </div>
            </div>

            {/* Podcast - Fixed version */}
            {note.podcastUrl && (
              <div className="mb-4">
                <h5>🎧 Podcast Version</h5>
                <Card>
                  <Card.Body>
                    {note.podcastUrl.includes('spotify.com/episode') ? (
                      // For regular Spotify episode URLs - convert to embed
                      <div>
                        <h6>Listen on Spotify</h6>
                        <div style={{
                          borderRadius: '12px',
                          overflow: 'hidden',
                          marginTop: '10px'
                        }}>
                          <iframe
                            src={`https://open.spotify.com/embed/episode/1vItocfM0y3e8MTIDdMmQN`}
                            width="100%"
                            height="152"
                            frameBorder="0"
                            allowTransparency="true"
                            allow="encrypted-media"
                            style={{border: 'none'}}
                            title="Spotify Podcast Player"
                          ></iframe>
                        </div>
                        <div className="mt-3">
                          <Button 
                            href={note.podcastUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            variant="success"
                            className="w-100"
                          >
                            🎵 Open in Spotify App
                          </Button>
                        </div>
                      </div>
                    ) : note.podcastUrl.includes('spotify.com/embed') ? (
                      // For Spotify embed URLs (if you change it later)
                      <div>
                        <h6>Listen on Spotify</h6>
                        <div style={{
                          borderRadius: '12px',
                          overflow: 'hidden',
                          marginTop: '10px'
                        }}>
                          <iframe
                            src={note.podcastUrl}
                            width="100%"
                            height="152"
                            frameBorder="0"
                            allowTransparency="true"
                            allow="encrypted-media"
                            style={{border: 'none'}}
                            title="Spotify Podcast Player"
                          ></iframe>
                        </div>
                        <div className="mt-3">
                          <Button 
                            href={note.podcastUrl.replace('/embed/', '/')} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            variant="success"
                            className="w-100"
                          >
                            🎵 Open in Spotify App
                          </Button>
                        </div>
                      </div>
                    ) : (
                      // For direct audio files
                      <div>
                        <h6>Listen to this content</h6>
                        <audio controls className="w-100 mt-2">
                          <source src={note.podcastUrl} type="audio/mpeg" />
                          Your browser does not support the audio element.
                        </audio>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </div>
            )}
          </article>
        </Col>
        
        <Col lg={4}>
          <Card className="sticky-top" style={{ top: '20px' }}>
            <Card.Header>
              <h6>About This Note</h6>
            </Card.Header>
            <Card.Body>
              <p><strong>Category:</strong> {note.category}</p>
              <p><strong>Published:</strong> {new Date(note.date).toLocaleDateString()}</p>
              <p><strong>Tags:</strong> {note.tags.join(', ')}</p>
              
              <div className="mt-3">
                <h6>Available Formats:</h6>
                <ul className="list-unstyled">
                  <li>📝 Written Notes</li>
                  {note.youtubeUrl && <li>🎥 Video Explanation</li>}
                  {note.podcastUrl && <li>🎧 Podcast Version</li>}
                </ul>
              </div>

              <div className="mt-4">
                <Button 
                  as={Link} 
                  to="/study-notes" 
                  variant="outline-secondary" 
                  size="sm"
                  className="w-100"
                >
                  ← Back to All Notes
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default StudyNoteDetail;